import React from 'react';
import { Button } from '../../../components/ui/button';
import { Download, Printer, Eye } from 'lucide-react';
import InsuranceLogo from '../../../components/InsuranceLogo';
import html2pdf from 'html2pdf.js';

/**
 * LeerFormContent - Gutachten Druckvorschau (Haftpflichtgutachten)
 * Extrahiert aus NeueAkte.jsx renderLeerForm (~1878 Zeilen)
 * 
 * Props:
 * - formData: Alle Formulardaten
 * - aktenzeichen: Aktenzeichen string
 * - getVersicherungsLogo: Funktion zum Laden von Versicherungslogos
 */

// Print Styles für A4 Dokument
const printStyles = `
  @media print {
    body * {
      visibility: hidden;
    }
    .printable-area,
    .printable-area * {
      visibility: visible;
    }
    .printable-area {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
    }
    .no-print {
      display: none !important;
    }
    .page-break { 
      page-break-after: always;
      page-break-inside: avoid;
    }
    .document-footer {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
    }
    @page {
      margin: 0;
      size: A4 portrait;
    }
    body {
      margin: 0;
      padding: 0;
    }
  }
  @media screen {
    .page-break { margin-bottom: 1rem; }
  }
`;

// Document Header Component - Fixed position with separator line
const DocumentHeader = () => (
  <div className="bg-white" style={{ height: '100px' }}>
    <div className="px-8 py-4">
      <div className="flex flex-col justify-center items-center">
        <img 
          src="/logo-new.png" 
          alt="Auto Gutachter24" 
          className="h-16 mb-1"
          style={{ width: 'auto' }}
        />
        <p className="text-gray-500 text-xs italic tracking-wide font-light">
          Ihr Partner bei Autounfällen
        </p>
      </div>
    </div>
    {/* Trennlinie unter Header */}
    <div className="border-b border-gray-200 mx-8"></div>
  </div>
);

// Document Footer Component - Fixed position with separator line
const DocumentFooter = ({ pageNumber }) => {
  const getUserData = () => {
    const storedUser = localStorage.getItem('autogutachter24_user');
    if (storedUser) {
      return JSON.parse(storedUser);
    }
    return {};
  };
  
  const userData = getUserData();
  
  return (
    <div className="bg-white w-full" style={{ position: 'absolute', bottom: '0', left: '0', right: '0', height: '85px' }}>
      {/* Trennlinie über Footer */}
      <div className="border-t border-gray-200 mx-8"></div>
      <div className="px-8 py-3">
        <div className="grid grid-cols-3 gap-4 text-xs">
          <div className="flex flex-col justify-start space-y-0.5">
            <p className="font-semibold text-gray-700 text-[10px]">
              {userData.vorname && userData.nachname 
                ? `${userData.vorname} ${userData.nachname}` 
                : userData.firma || 'Admin Demo'}
            </p>
            <p className="text-gray-500 text-[9px]">
              {userData.strasse || 'Musterstraße'} {userData.hausnummer || '123'}, {userData.plz || '80331'} {userData.ort || 'München'}
            </p>
            <p className="text-gray-500 text-[9px]">Tel: {userData.telefon || '+49 89 1234567'}</p>
          </div>
          <div className="flex flex-col justify-center items-center">
            <p className="text-gray-600 text-[10px]">{userData.email || 'info@autogutachter24.de'}</p>
            <p className="text-gray-600 text-[10px]">{userData.website || 'www.autogutachter24.com'}</p>
            {pageNumber && (
              <p className="text-gray-800 font-semibold text-xs mt-1">Seite {pageNumber}</p>
            )}
          </div>
          <div className="flex flex-col justify-start items-end space-y-0.5">
            <p className="text-gray-500 text-[9px]">{userData.bankname || 'Sparkasse München'}</p>
            <p className="text-gray-500 text-[9px]">IBAN: {userData.iban || 'DE89 3704 0044 0532 0130 00'}</p>
            <p className="text-gray-500 text-[9px]">UST-ID: {userData.ust_id || 'DE123456789'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const LeerFormContent = ({ formData, aktenzeichen, getVersicherungsLogo }) => {
  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <style>{printStyles}</style>
      
      <div className="printable-area max-w-[210mm] mx-auto">
        {/* Seite 1: Deckblatt / Titelseite */}
        <div className="bg-white shadow-lg relative" style={{ width: '210mm', minHeight: '297mm', paddingBottom: '85px' }}>
          <DocumentHeader />

          {/* Deckblatt Content - Vertikal zentriert */}
          <div className="flex flex-col items-center justify-center px-10" style={{ minHeight: 'calc(297mm - 160px)', paddingTop: '40px' }}>
            
            {/* Haupttitel - Dynamisch basierend auf Auftragstyp */}
            <div className="text-center mb-10">
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                {formData.auftragstyp === 'Gutachten' ? 'Haftpflichtgutachten' :
                 formData.auftragstyp === 'Kurzgutachten' ? 'Kurzgutachten' :
                 formData.auftragstyp === 'Wertgutachten' ? 'Wertgutachten' :
                 formData.auftragstyp === 'Kasko' ? 'Kasko-Gutachten' :
                 formData.auftragstyp === 'Reparaturbestätigung' ? 'Reparaturbestätigung' :
                 formData.auftragstyp === 'Stellungnahme' ? 'Stellungnahme' :
                 formData.auftragstyp || 'Haftpflichtgutachten'}
              </h1>
              <p className="text-lg text-gray-600 mt-2">
                {formData.auftragstyp === 'Kurzgutachten' ? 'Kurzbewertung' :
                 formData.auftragstyp === 'Wertgutachten' ? 'Fahrzeugbewertung' :
                 formData.auftragstyp === 'Kasko' ? 'Kaskoschadenmeldung' :
                 formData.auftragstyp === 'Reparaturbestätigung' ? 'Reparaturnachweis' :
                 'Schadensmeldung'}
              </p>
              <div className="w-32 h-1 bg-blue-600 mx-auto mt-4"></div>
            </div>

            {/* Kundendaten - schmaler */}
            <div className="max-w-md mx-auto mb-8">
              <div className="p-5">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 text-center">Anspruchsteller</h3>
                <div className="space-y-1 text-center">
                  <p className="text-xl font-bold text-gray-900">{formData.kunde || 'Kunde'}</p>
                  <p className="text-sm text-gray-600">{formData.adresse1 || 'Adresse'} {formData.adresse2 || ''}</p>
                  <p className="text-sm text-gray-700 font-semibold mt-2">Aktenzeichen: {aktenzeichen || formData.aktenzeichen || '-'}</p>
                </div>
              </div>
            </div>

            {/* Fahrzeugdaten mit Logo - gleiche Breite wie Versicherung */}
            <div className="max-w-lg mx-auto mb-8">
              <div className="bg-blue-50 rounded-lg p-5">
                <h3 className="text-sm font-semibold text-blue-700 uppercase tracking-wide mb-3 text-center">Fahrzeug</h3>
                
                {formData.marke && (
                  <div className="flex justify-center mb-3">
                    <img 
                      src={`https://logo.clearbit.com/${formData.marke.toLowerCase()}.com`}
                      alt={formData.marke}
                      className="h-10 object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                
                <div className="space-y-1 text-center">
                  <p className="text-2xl font-bold text-blue-900">{formData.kennzeichen || 'Kennzeichen'}</p>
                  <p className="text-sm text-gray-700">{formData.marke || 'Marke'} {formData.modell || 'Modell'}</p>
                </div>
              </div>
            </div>

            {/* Gegnerische Versicherung - ohne Außenlinie */}
            <div className="max-w-lg mx-auto mb-8">
              <div className="bg-gray-50 rounded-lg p-5">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4 text-center">Gegnerische Versicherung</h3>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between border-b border-gray-200 pb-1">
                    <span className="text-gray-500">Versicherung:</span>
                    <span className="text-gray-800 font-medium">{formData.versicherungsname || '-'}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-200 pb-1">
                    <span className="text-gray-500">Vers.-Schein-Nr.:</span>
                    <span className="text-gray-800 font-medium">{formData.versicherungsscheinnummer || '-'}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-200 pb-1">
                    <span className="text-gray-500">Schadentag:</span>
                    <span className="text-gray-800 font-medium">{formData.schadentag || '-'}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-200 pb-1">
                    <span className="text-gray-500">Schadenort:</span>
                    <span className="text-gray-800 font-medium">{formData.schadenort || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Kennzeichen Gegner:</span>
                    <span className="text-gray-800 font-medium">{formData.gegner_kennzeichen || '-'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Besichtigungstag - schmaler */}
            <div className="max-w-sm mx-auto">
              <div className="border-t border-b border-gray-200 py-3">
                <div className="text-center">
                  <p className="text-xs text-gray-500 uppercase">Besichtigungstag</p>
                  <p className="text-lg font-semibold text-gray-800">{formData.besichtigungsdatum || '-'}</p>
                </div>
              </div>
            </div>

          </div>

          <DocumentFooter pageNumber={1} />
        </div>

        {/* Page Break */}
        <div className="page-break"></div>

        {/* Seite 2: 1. Auftrag - Neues Layout nach Vorlage */}
        <div className="bg-white shadow-lg relative mt-8" style={{ width: '210mm', minHeight: '297mm', paddingBottom: '85px' }}>
          <DocumentHeader />
          
          <div className="px-10 py-6">
            {/* Section Header mit Nummer und Aktenzeichen */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white flex items-center justify-center font-bold text-xl mr-4 shadow-md">1</div>
                <h2 className="text-2xl font-bold text-gray-800">Auftrag</h2>
              </div>
              {/* Aktenzeichen hervorgehoben */}
              <div className="border-2 border-emerald-500 rounded-lg px-5 py-3 bg-gradient-to-br from-emerald-50 to-green-50 shadow-sm">
                <p className="text-xs text-emerald-600 uppercase tracking-wider font-semibold">Aktenzeichen</p>
                <p className="text-lg font-bold text-gray-800">{aktenzeichen || formData.aktenzeichen || '-'}</p>
              </div>
            </div>
            
            {/* Kundendaten - Volle Breite mit Hintergrund */}
            <div className="mb-5 bg-gradient-to-r from-gray-50 to-slate-50 rounded-lg p-4">
              <h3 className="text-sm font-bold text-blue-700 uppercase tracking-wide mb-3">Kundendaten</h3>
              <div className="grid grid-cols-1 gap-1 text-sm pl-2">
                <p className="text-gray-900 font-semibold text-base">{formData.kunde || '-'}</p>
                <p className="text-gray-600">{formData.adresse1 || '-'}</p>
                <p className="text-gray-600">{formData.plz || ''} {formData.ort || ''}</p>
                <p className="text-gray-500 mt-2">Fahrzeugtyp: <span className="font-semibold text-blue-600 bg-blue-100 px-2 py-0.5 rounded">{formData.fahrzeugtyp || 'PKW'}</span></p>
              </div>
            </div>

            {/* Schadendaten und Auftragsdaten nebeneinander */}
            <div className="grid grid-cols-2 gap-6 mb-5">
              {/* Schadendaten */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-bold text-blue-700 uppercase tracking-wide mb-3 pb-2 border-b-2 border-blue-200">Schadendaten</h3>
                <div className="space-y-2.5 text-sm">
                  <div className="flex justify-between items-center py-1 border-b border-gray-100">
                    <span className="text-gray-500">Schadentag:</span>
                    <span className="text-gray-800 font-semibold bg-amber-50 px-2 py-0.5 rounded">{formData.schadentag || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-gray-100">
                    <span className="text-gray-500">Anfrageart:</span>
                    <span className="text-gray-800 font-medium">{formData.anfrageart || formData.auftragtyp || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-gray-100">
                    <span className="text-gray-500">Schadenart:</span>
                    <span className="text-gray-800 font-medium">{formData.schadenart || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-gray-100">
                    <span className="text-gray-500">Schadennummer:</span>
                    <span className="text-gray-800 font-medium">{formData.schadennummer || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-gray-500">Schadenort:</span>
                    <span className="text-gray-800 font-medium">{formData.schadenort || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Auftragsdaten */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-bold text-blue-700 uppercase tracking-wide mb-3 pb-2 border-b-2 border-blue-200">Auftragsdaten</h3>
                <div className="space-y-2.5 text-sm">
                  <div className="flex justify-between items-center py-1 border-b border-gray-100">
                    <span className="text-gray-500">Welche Scheibe:</span>
                    <span className="text-gray-800 font-medium">{formData.welche_scheibe || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-gray-100">
                    <span className="text-gray-500">Auftragstyp:</span>
                    <span className="text-gray-800 font-semibold text-blue-700">{formData.auftragstyp || formData.auftragtyp || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-gray-500">Vorsteuerabzug:</span>
                    <span className={`font-semibold px-2 py-0.5 rounded ${formData.vorsteuerabzug === 'ja' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{formData.vorsteuerabzug === 'ja' ? 'Ja' : 'Nein'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Fahrzeugdaten - Zwei Spalten mit Hintergrund */}
            <div className="mb-5 bg-slate-50 rounded-lg p-4">
              <h3 className="text-sm font-bold text-blue-700 uppercase tracking-wide mb-3 pb-2 border-b-2 border-blue-200">Fahrzeugdaten</h3>
              <div className="grid grid-cols-2 gap-6 text-sm">
                {/* Linke Spalte */}
                <div className="space-y-2.5">
                  <div className="flex justify-between items-center py-1 border-b border-gray-200">
                    <span className="text-gray-500">Kennzeichen:</span>
                    <span className="text-gray-900 font-bold bg-yellow-100 px-2 py-0.5 rounded border border-yellow-300">{formData.kennzeichen || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-gray-200">
                    <span className="text-gray-500">Modell:</span>
                    <span className="text-gray-800 font-medium">{formData.modell || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-gray-200">
                    <span className="text-gray-500">FIN:</span>
                    <span className="text-gray-700 font-medium font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">{formData.vin || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-gray-500">Selbstbeteiligung:</span>
                    <span className="text-gray-800 font-semibold">{formData.selbstbeteiligung || '0'} €</span>
                  </div>
                </div>
                {/* Rechte Spalte */}
                <div className="space-y-2.5">
                  <div className="flex justify-between items-center py-1 border-b border-gray-200">
                    <span className="text-gray-500">Marke:</span>
                    <span className="text-gray-800 font-semibold">{formData.marke || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-gray-200">
                    <span className="text-gray-500">Baujahr:</span>
                    <span className="text-gray-800 font-medium">{formData.baujahr || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-gray-200">
                    <span className="text-gray-500">Vers.-Nummer:</span>
                    <span className="text-gray-800 font-medium">{formData.vers_nummer || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-gray-500">Erstzulassung:</span>
                    <span className="text-gray-800 font-medium">{formData.erstzulassung || '-'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Zusammenfassung */}
            <div className="mb-5">
              <h3 className="text-sm font-bold text-blue-700 uppercase tracking-wide mb-3 pb-2 border-b-2 border-blue-200">Zusammenfassung</h3>
              <div className="overflow-hidden rounded-lg shadow-sm">
                <table className="w-full text-sm">
                  <thead className="bg-gradient-to-r from-blue-600 to-blue-700">
                    <tr>
                      <th className="text-left py-2.5 px-4 text-white font-semibold"></th>
                      <th className="text-right py-2.5 px-4 text-white font-semibold text-xs">ohne MwSt.</th>
                      <th className="text-right py-2.5 px-4 text-white font-semibold text-xs">mit MwSt.</th>
                      <th className="text-right py-2.5 px-4 text-white font-semibold text-xs">Tage</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <tr className="bg-white hover:bg-gray-50">
                      <td className="py-2 px-4 text-gray-700 font-medium">Schadenhöhe</td>
                      <td className="py-2 px-4 text-right text-gray-800">{formData.schadenhoehe_netto ? parseFloat(formData.schadenhoehe_netto).toLocaleString('de-DE', {minimumFractionDigits: 2}) : '0,00'} €</td>
                      <td className="py-2 px-4 text-right text-gray-800 font-semibold">{formData.schadenhoehe_brutto ? parseFloat(formData.schadenhoehe_brutto).toLocaleString('de-DE', {minimumFractionDigits: 2}) : '0,00'} €</td>
                      <td className="py-2 px-4 text-right text-gray-400">-</td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100">
                      <td className="py-2 px-4 text-gray-700 font-medium">Reparaturkosten</td>
                      <td className="py-2 px-4 text-right text-gray-800">{formData.reparaturkosten_netto ? parseFloat(formData.reparaturkosten_netto).toLocaleString('de-DE', {minimumFractionDigits: 2}) : '0,00'} €</td>
                      <td className="py-2 px-4 text-right text-gray-800 font-semibold">{formData.reparaturkosten_brutto ? parseFloat(formData.reparaturkosten_brutto).toLocaleString('de-DE', {minimumFractionDigits: 2}) : '0,00'} €</td>
                      <td className="py-2 px-4 text-right text-gray-400">-</td>
                    </tr>
                    <tr className="bg-white hover:bg-gray-50">
                      <td className="py-2 px-4 text-gray-700 font-medium">Wiederbeschaffungswert</td>
                      <td className="py-2 px-4 text-right text-gray-800">{formData.wiederbeschaffungswert_netto ? parseFloat(formData.wiederbeschaffungswert_netto).toLocaleString('de-DE', {minimumFractionDigits: 2}) : '0,00'} €</td>
                      <td className="py-2 px-4 text-right text-gray-800 font-semibold">{formData.wiederbeschaffungswert_brutto ? parseFloat(formData.wiederbeschaffungswert_brutto).toLocaleString('de-DE', {minimumFractionDigits: 2}) : '0,00'} €</td>
                      <td className="py-2 px-4 text-right text-gray-400">-</td>
                    </tr>
                    <tr className="bg-amber-50 hover:bg-amber-100">
                      <td className="py-2 px-4 text-gray-700 font-medium">Restwert</td>
                      <td className="py-2 px-4 text-right text-amber-700 font-semibold" colSpan="2">{formData.restwert ? parseFloat(formData.restwert).toLocaleString('de-DE', {minimumFractionDigits: 2}) + ' €' : '-'}</td>
                      <td className="py-2 px-4 text-right text-gray-400">-</td>
                    </tr>
                    <tr className="bg-white hover:bg-gray-50">
                      <td className="py-2 px-4 text-gray-700 font-medium">Reparaturdauer</td>
                      <td className="py-2 px-4 text-right text-gray-400" colSpan="2">-</td>
                      <td className="py-2 px-4 text-right text-blue-600 font-semibold">{formData.reparaturdauer || '-'} Tage</td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100">
                      <td className="py-2 px-4 text-gray-700 font-medium">Wiederbeschaffungsdauer</td>
                      <td className="py-2 px-4 text-right text-gray-400" colSpan="2">-</td>
                      <td className="py-2 px-4 text-right text-blue-600 font-semibold">{formData.wiederbeschaffungsdauer || '-'} Tage</td>
                    </tr>
                    <tr className="bg-white hover:bg-gray-50">
                      <td className="py-2 px-4 text-gray-700 font-medium">Nutzungsausfall</td>
                      <td className="py-2 px-4 text-right text-gray-800 font-semibold" colSpan="2">{formData.nutzungsausfall ? parseFloat(formData.nutzungsausfall).toLocaleString('de-DE', {minimumFractionDigits: 2}) + ' €' : '0,00 €'}</td>
                      <td className="py-2 px-4 text-right text-gray-400">-</td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100">
                      <td className="py-2 px-4 text-gray-700 font-medium">Mietwagenklasse</td>
                      <td className="py-2 px-4 text-right text-gray-800" colSpan="3">{formData.mietwagenklasse || '-'}</td>
                    </tr>
                    <tr className="bg-white hover:bg-gray-50">
                      <td className="py-2 px-4 text-gray-700 font-medium">Reparaturbeurteilung</td>
                      <td className="py-2 px-4 text-right text-gray-800" colSpan="3">{formData.reparaturbeurteilung || '-'}</td>
                    </tr>
                    <tr className="bg-green-50 hover:bg-green-100">
                      <td className="py-2 px-4 text-gray-700 font-medium">Fahrbereitschaft</td>
                      <td className="py-2 px-4 text-right font-semibold" colSpan="3">
                        <span className={`px-2 py-0.5 rounded ${formData.fahrbereitschaft === 'Ja' ? 'bg-green-200 text-green-800' : formData.fahrbereitschaft === 'Nein' ? 'bg-red-200 text-red-800' : 'text-gray-600'}`}>
                          {formData.fahrbereitschaft || '-'}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <DocumentFooter pageNumber={2} />
        </div>

        {/* Page Break */}
        <div className="page-break"></div>

        {/* Seite 3: 2. Versicherung */}
        <div className="bg-white shadow-lg relative mt-8" style={{ width: '210mm', minHeight: '297mm', paddingBottom: '85px' }}>
          <DocumentHeader />
          
          <div className="px-10 py-6">
            {/* Section Header mit Nummer */}
            <div className="flex items-center mb-8">
              <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg mr-4">2</div>
              <h2 className="text-2xl font-bold text-gray-800">Versicherung</h2>
            </div>
            
            {/* Gegnerische Versicherung */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-3">Gegnerische Versicherung</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex border-b border-gray-200 pb-2">
                  <span className="text-gray-500 w-48 text-sm">Versicherung:</span>
                  <span className="text-gray-800 font-semibold text-sm">{formData.versicherungsname || '-'}</span>
                </div>
                <div className="flex border-b border-gray-200 pb-2">
                  <span className="text-gray-500 w-48 text-sm">Schadennummer:</span>
                  <span className="text-gray-800 font-medium text-sm">{formData.schadennummer || '-'}</span>
                </div>
                <div className="flex border-b border-gray-200 pb-2">
                  <span className="text-gray-500 w-48 text-sm">Vers.-Schein-Nr.:</span>
                  <span className="text-gray-800 font-medium text-sm">{formData.versicherungsscheinnummer || '-'}</span>
                </div>
                <div className="flex">
                  <span className="text-gray-500 w-48 text-sm">Anschrift:</span>
                  <span className="text-gray-800 font-medium text-sm">
                    {formData.versicherung_strasse || '-'}, {formData.versicherung_plz || ''} {formData.versicherung_ort || ''}
                  </span>
                </div>
              </div>
            </div>

            {/* Unfallgegner */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-3">Unfallgegner</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex border-b border-gray-200 pb-2">
                  <span className="text-gray-500 w-48 text-sm">Name:</span>
                  <span className="text-gray-800 font-medium text-sm">{formData.gegner_vorname || ''} {formData.gegner_name || '-'}</span>
                </div>
                <div className="flex border-b border-gray-200 pb-2">
                  <span className="text-gray-500 w-48 text-sm">Kennzeichen:</span>
                  <span className="text-gray-800 font-semibold text-sm">{formData.gegner_kennzeichen || '-'}</span>
                </div>
                <div className="flex border-b border-gray-200 pb-2">
                  <span className="text-gray-500 w-48 text-sm">Adresse:</span>
                  <span className="text-gray-800 font-medium text-sm">
                    {formData.gegner_strasse || '-'}, {formData.gegner_plz || ''} {formData.gegner_ort || ''}
                  </span>
                </div>
                <div className="flex">
                  <span className="text-gray-500 w-48 text-sm">Fahrzeug:</span>
                  <span className="text-gray-800 font-medium text-sm">{formData.gegner_fahrzeug_marke || '-'} {formData.gegner_fahrzeug_modell || ''}</span>
                </div>
              </div>
            </div>
          </div>

          <DocumentFooter pageNumber={3} />
        </div>

        {/* Page Break */}
        <div className="page-break"></div>

        {/* Seite 4: Abtretungserklärung */}
        <div className="bg-white shadow-lg relative mt-8" style={{ width: '210mm', minHeight: '297mm', paddingBottom: '85px' }}>
          <DocumentHeader />
          
          <div className="px-14 py-8">
            {/* Titel */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-light tracking-widest text-gray-800 uppercase">Abtretungserklärung</h2>
              <p className="text-xs text-gray-500 mt-2 tracking-wide">Auftrag und Abtretung von Sachverständigenkosten</p>
            </div>

            {/* Parteien-Box */}
            <div className="bg-gray-50 p-6 mb-8">
              <div className="grid grid-cols-2 gap-12">
                {/* Sachverständigenbüro */}
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Sachverständigenbüro</p>
                  <p className="font-semibold text-gray-900">AutoGutachter24</p>
                  <p className="text-sm text-gray-700">Musterstraße 123</p>
                  <p className="text-sm text-gray-700">80331 München</p>
                  <p className="text-sm text-gray-700 mt-2">Tel: +49 89 1234567</p>
                </div>

                {/* Anspruchsteller */}
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Auftraggeber / Anspruchsteller</p>
                  <p className="font-semibold text-gray-900">{formData.kunde || '—'}</p>
                  <p className="text-sm text-gray-700">{formData.adresse1 || '—'}</p>
                  <p className="text-sm text-gray-700 mt-2">Tel: {formData.mobilnummer || formData.telefon || '—'}</p>
                  <p className="text-sm text-gray-700">E-Mail: {formData.email || '—'}</p>
                </div>
              </div>
            </div>

            {/* Schadendetails */}
            <div className="grid grid-cols-4 gap-6 mb-8 text-sm">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Aktenzeichen</p>
                <p className="font-medium text-gray-900">{formData.aktenzeichen || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Schadentag</p>
                <p className="font-medium text-gray-900">{formData.schadentag || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Schadenort</p>
                <p className="font-medium text-gray-900">{formData.schadenort || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Kennzeichen</p>
                <p className="font-medium text-gray-900">{formData.kennzeichen || '—'}</p>
              </div>
            </div>

            {/* Schädiger / Versicherung */}
            <div className="bg-gray-50 p-6 mb-8">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-4">Gegnerische Haftpflichtversicherung</p>
              <div className="grid grid-cols-3 gap-6 text-sm">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Versicherung</p>
                  <p className="font-medium text-gray-900">{formData.versicherungsname || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Schadennummer</p>
                  <p className="font-medium text-gray-900">{formData.schadennummer || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Vers.-Schein-Nr.</p>
                  <p className="font-medium text-gray-900">{formData.versicherungsscheinnummer || '—'}</p>
                </div>
              </div>
            </div>

            {/* Abtretungstext */}
            <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
              <p>
                Hiermit beauftrage ich das Sachverständigenbüro AutoGutachter24 mit der Erstellung eines 
                Schadensgutachtens. Das Honorar wird nach der umseitig aufgeführten Honorartabelle berechnet.
              </p>

              <p>
                Zur Erfüllung meiner Zahlungsverpflichtung trete ich hiermit meinen Anspruch auf Erstattung 
                der Sachverständigenkosten <span className="font-semibold text-gray-900">unwiderruflich erstrangig 
                erfüllungshalber</span> an das Sachverständigenbüro AutoGutachter24 gegen den Fahrer, den Halter 
                und den Haftpflichtversicherer des gegnerischen Fahrzeuges ab.
              </p>

              <p>
                Das Sachverständigenbüro ist berechtigt, die Abtretung gegenüber den Anspruchsgegnern 
                offen zu legen und den abgetretenen Anspruch im eigenen Namen geltend zu machen.
              </p>

              <p>
                Durch die Abtretung werde ich nicht von meiner Zahlungsverpflichtung befreit. 
                Der Auftraggeber verpflichtet sich, über den abgetretenen Anspruch keine anderweitigen 
                Vereinbarungen mit den Anspruchsgegnern zu treffen.
              </p>
            </div>

            {/* Datenschutz */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Datenschutz</p>
              <p className="text-xs text-gray-600 leading-relaxed">
                Ich bin damit einverstanden, dass meine personenbezogenen Daten für die Erstellung des 
                Schadensgutachtens verarbeitet und an Dritte zur Schadensabwicklung weitergegeben werden dürfen.
              </p>
            </div>
          </div>

          <DocumentFooter pageNumber={4} />
        </div>

        {/* Page Break */}
        <div className="page-break"></div>

        {/* Seite 5: Widerrufsrecht und Unterschriften */}
        <div className="bg-white shadow-lg relative mt-8" style={{ width: '210mm', minHeight: '297mm', paddingBottom: '85px' }}>
          <DocumentHeader />
          
          <div className="px-14 py-8">
            {/* Widerrufsrecht */}
            <div className="mb-8">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-4">Widerrufsbelehrung</p>
              <div className="text-sm text-gray-700 leading-relaxed space-y-3">
                <p>
                  Sie haben das Recht, binnen <span className="font-semibold">14 Tagen</span> ohne Angabe von Gründen 
                  diesen Vertrag zu widerrufen. Um Ihr Widerrufsrecht auszuüben, müssen Sie uns mittels einer 
                  eindeutigen Erklärung informieren.
                </p>
                <p>
                  Haben Sie verlangt, dass die Dienstleistungen während der Widerrufsfrist beginnen sollen, 
                  haben Sie uns einen angemessenen Betrag für die bereits erbrachten Leistungen zu zahlen.
                </p>
              </div>
            </div>

            {/* Erklärungen */}
            <div className="bg-gray-50 p-6 mb-8">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-4">Bestätigungen</p>
              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex items-start">
                  <span className="w-5 h-5 border-2 border-gray-400 rounded flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <span className="text-gray-600 text-xs">✓</span>
                  </span>
                  <p>Ich akzeptiere die Datenschutzbestimmungen und die AGB.</p>
                </div>
                <div className="flex items-start">
                  <span className="w-5 h-5 border-2 border-gray-400 rounded flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <span className="text-gray-600 text-xs">✓</span>
                  </span>
                  <p>Ich bestätige den Erhalt der Widerrufsbelehrung.</p>
                </div>
                <div className="flex items-start">
                  <span className="w-5 h-5 border-2 border-gray-400 rounded flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <span className="text-gray-600 text-xs">✓</span>
                  </span>
                  <p>Ich verlange ausdrücklich, dass mit der Dienstleistung vor Ablauf der Widerrufsfrist begonnen wird.</p>
                </div>
              </div>
            </div>

            {/* Unterschriften */}
            <div className="grid grid-cols-2 gap-16 mb-12">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-6">Auftraggeber</p>
                <p className="text-sm text-gray-700 mb-8">
                  {formData.schadenort || '____________'}, {formData.abtretung_datum || new Date().toLocaleDateString('de-DE')}
                </p>
                <div className="border-b border-gray-400 mb-2 h-12"></div>
                <p className="text-xs text-gray-500">{formData.kunde || 'Unterschrift Auftraggeber'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-6">Sachverständiger</p>
                <p className="text-sm text-gray-700 mb-8">
                  München, {new Date().toLocaleDateString('de-DE')}
                </p>
                <div className="border-b border-gray-400 mb-2 h-12"></div>
                <p className="text-xs text-gray-500">AutoGutachter24</p>
              </div>
            </div>

            {/* Honorartabelle */}
            <div className="bg-gray-50 p-6">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-4">Honorartabelle (netto zzgl. MwSt.)</p>
              <div className="grid grid-cols-6 gap-4 text-xs">
                <div className="space-y-2">
                  <p className="font-medium text-gray-500 pb-2 border-b border-gray-300">Schaden bis</p>
                  <p className="text-gray-700">500 €</p>
                  <p className="text-gray-700">1.000 €</p>
                  <p className="text-gray-700">1.500 €</p>
                  <p className="text-gray-700">2.000 €</p>
                  <p className="text-gray-700">2.500 €</p>
                </div>
                <div className="space-y-2">
                  <p className="font-medium text-gray-500 pb-2 border-b border-gray-300">Honorar</p>
                  <p className="text-gray-900 font-medium">178 €</p>
                  <p className="text-gray-900 font-medium">228 €</p>
                  <p className="text-gray-900 font-medium">268 €</p>
                  <p className="text-gray-900 font-medium">303 €</p>
                  <p className="text-gray-900 font-medium">334 €</p>
                </div>
                <div className="space-y-2">
                  <p className="font-medium text-gray-500 pb-2 border-b border-gray-300">Schaden bis</p>
                  <p className="text-gray-700">3.000 €</p>
                  <p className="text-gray-700">4.000 €</p>
                  <p className="text-gray-700">5.000 €</p>
                  <p className="text-gray-700">6.000 €</p>
                  <p className="text-gray-700">7.000 €</p>
                </div>
                <div className="space-y-2">
                  <p className="font-medium text-gray-500 pb-2 border-b border-gray-300">Honorar</p>
                  <p className="text-gray-900 font-medium">364 €</p>
                  <p className="text-gray-900 font-medium">418 €</p>
                  <p className="text-gray-900 font-medium">467 €</p>
                  <p className="text-gray-900 font-medium">512 €</p>
                  <p className="text-gray-900 font-medium">554 €</p>
                </div>
                <div className="space-y-2">
                  <p className="font-medium text-gray-500 pb-2 border-b border-gray-300">Schaden bis</p>
                  <p className="text-gray-700">8.000 €</p>
                  <p className="text-gray-700">9.000 €</p>
                  <p className="text-gray-700">10.000 €</p>
                  <p className="text-gray-700">12.500 €</p>
                  <p className="text-gray-700">15.000 €</p>
                </div>
                <div className="space-y-2">
                  <p className="font-medium text-gray-500 pb-2 border-b border-gray-300">Honorar</p>
                  <p className="text-gray-900 font-medium">594 €</p>
                  <p className="text-gray-900 font-medium">631 €</p>
                  <p className="text-gray-900 font-medium">667 €</p>
                  <p className="text-gray-900 font-medium">750 €</p>
                  <p className="text-gray-900 font-medium">828 €</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4">
                Höhere Schadenbeträge nach BVSK-Honorartabelle. Nebenkosten werden separat berechnet.
              </p>
            </div>
          </div>

          <DocumentFooter pageNumber={5} />
        </div>

        {/* Page Break */}
        <div className="page-break"></div>

        {/* Seite 6: 4. Begutachtung */}
        <div className="bg-white shadow-lg relative mt-8" style={{ width: '210mm', minHeight: '297mm', paddingBottom: '85px' }}>
          <DocumentHeader />
          
          <div className="px-14 py-8">
            {/* Titel */}
            <div className="text-center mb-10">
              <h2 className="text-2xl font-light tracking-widest text-gray-800 uppercase">Begutachtung</h2>
              <p className="text-xs text-gray-500 mt-2 tracking-wide">Dokumentation der Fahrzeugbesichtigung</p>
            </div>
            
            {/* Besichtigungsdaten */}
            <div className="mb-10">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-5">Besichtigungsdaten</p>
              <div className="grid grid-cols-3 gap-x-8 gap-y-5 text-sm">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Datum</p>
                  <p className="font-medium text-gray-900">{formData.besichtigungsdatum || formData.schadentag || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Ort</p>
                  <p className="font-medium text-gray-900">{formData.besichtigungsort || formData.schadenort || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Bedingungen</p>
                  <p className="font-medium text-gray-900">{formData.besichtigungsbedingungen || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Zustand bei Besichtigung</p>
                  <p className="font-medium text-gray-900">{formData.besichtigungszustand || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Fahrbereitschaft</p>
                  <p className="font-medium text-gray-900">{formData.fahrbereitschaft || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Verkehrssicherheit</p>
                  <p className="font-medium text-gray-900">{formData.verkehrssicherheit || '—'}</p>
                </div>
              </div>
            </div>

            {/* Feststellungen bei der Besichtigung */}
            {(formData.dokumentationstext || formData.zustandstext || formData.identifizierungstext) && (
              <div className="mb-10">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-5">Feststellungen bei der Besichtigung</p>
                <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
                  {formData.dokumentationstext && (
                    <p>{formData.dokumentationstext}</p>
                  )}
                  {formData.zustandstext && (
                    <p>{formData.zustandstext}</p>
                  )}
                  {formData.identifizierungstext && (
                    <p>{formData.identifizierungstext}</p>
                  )}
                </div>
              </div>
            )}

            {/* Fahrzeugzustand */}
            {(formData.allgemeinzustandtext || formData.karosseriezustandtext || formData.lackzustandtext || formData.innenraumzustandtext || formData.zustandsbeschreibung) && (
              <div className="mb-10">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-5">Fahrzeugzustand</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-5 text-sm">
                  {formData.allgemeinzustandtext && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Allgemeinzustand</p>
                      <p className="text-gray-800">{formData.allgemeinzustandtext}</p>
                    </div>
                  )}
                  {formData.karosseriezustandtext && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Karosserie</p>
                      <p className="text-gray-800">{formData.karosseriezustandtext}</p>
                    </div>
                  )}
                  {formData.lackzustandtext && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Lack</p>
                      <p className="text-gray-800">{formData.lackzustandtext}</p>
                    </div>
                  )}
                  {formData.innenraumzustandtext && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Innenraum</p>
                      <p className="text-gray-800">{formData.innenraumzustandtext}</p>
                    </div>
                  )}
                </div>
                {formData.zustandsbeschreibung && (
                  <div className="mt-5">
                    <p className="text-xs text-gray-500 mb-1">Ergänzende Beschreibung</p>
                    <p className="text-sm text-gray-800">{formData.zustandsbeschreibung}</p>
                  </div>
                )}
              </div>
            )}

            {/* Schadenhergang */}
            {(formData.schadenhergangtext || formData.unfallprotokolltext || formData.plausibilitaettext || formData.kompatibilitaettext || formData.detaillierte_schilderung) && (
              <div className="mb-10">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-5">Schadenhergang</p>
                <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
                  {formData.schadenhergangtext && (
                    <p>{formData.schadenhergangtext}</p>
                  )}
                  {formData.unfallprotokolltext && (
                    <p>{formData.unfallprotokolltext}</p>
                  )}
                  {formData.detaillierte_schilderung && (
                    <p>{formData.detaillierte_schilderung}</p>
                  )}
                  {(formData.plausibilitaettext || formData.kompatibilitaettext) && (
                    <div className="mt-4 space-y-4">
                      {formData.plausibilitaettext && (
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Plausibilitätsprüfung</p>
                          <p className="text-gray-800">{formData.plausibilitaettext}</p>
                        </div>
                      )}
                      {formData.kompatibilitaettext && (
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Kompatibilitätsprüfung</p>
                          <p className="text-gray-800">{formData.kompatibilitaettext}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Durchgeführte Prüfungen */}
            {(formData.scheckhefttext || formData.probelauftext || formData.probefahrttext || formData.fehlerspeichertext) && (
              <div className="mb-10">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-5">Durchgeführte Prüfungen</p>
                <div className="grid grid-cols-4 gap-6 text-sm">
                  {formData.scheckhefttext && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Scheckheft</p>
                      <p className="font-medium text-gray-900">{formData.scheckhefttext}</p>
                    </div>
                  )}
                  {formData.probelauftext && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Probelauf</p>
                      <p className="font-medium text-gray-900">{formData.probelauftext}</p>
                    </div>
                  )}
                  {formData.probefahrttext && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Probefahrt</p>
                      <p className="font-medium text-gray-900">{formData.probefahrttext}</p>
                    </div>
                  )}
                  {formData.fehlerspeichertext && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Fehlerspeicher</p>
                      <p className="font-medium text-gray-900">{formData.fehlerspeichertext}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Schadenbeschreibung */}
            <div className="mb-10">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-5">Schadenbeschreibung</p>
              <div className="text-sm text-gray-700 leading-relaxed">
                <p className="whitespace-pre-wrap">
                  {formData.schadenbeschreibung || formData.schadenfeststellung || 'Keine Schadenbeschreibung vorhanden.'}
                </p>
              </div>
            </div>

            {/* Vorschäden */}
            <div className="mb-10">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-5">Vorschäden / Altschäden</p>
              <p className="text-gray-700 text-sm leading-relaxed">
                {formData.vorschaeden || formData.vorschaeden_text || 'Keine Vorschäden bekannt.'}
              </p>
            </div>

            {/* Reparaturbeurteilung */}
            <div className="mb-10">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-5">Reparaturbeurteilung</p>
              <p className="text-gray-700 text-sm leading-relaxed">
                {formData.reparaturbeurteilung || formData.beurteilungstext || 'Keine Beurteilung vorhanden.'}
              </p>
            </div>
          </div>

          <DocumentFooter pageNumber={6} />
        </div>

        {/* Page Break */}
        <div className="page-break"></div>

        {/* Seite 7: 5. Lichtbilder */}
        <div className="bg-white shadow-lg relative mt-8" style={{ width: '210mm', minHeight: '297mm', paddingBottom: '85px' }}>
          <DocumentHeader />
          
          <div className="px-10 py-6">
            {/* Section Header mit Nummer */}
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg mr-4">5</div>
              <h2 className="text-2xl font-bold text-gray-800">Lichtbilder</h2>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600">Fotodokumentation des Schadens</p>
            </div>

            {/* Erste 4 Bilder */}
            {formData.lichtbilder && formData.lichtbilder.length > 0 ? (
              <div className="grid grid-cols-2 gap-4">
                {formData.lichtbilder.slice(0, 4).map((bild, index) => {
                  let bildUrl = typeof bild === 'string' ? bild : (bild.url || bild.preview || bild.path || '');
                  const bildName = typeof bild === 'object' ? (bild.original_name || bild.name || bild.beschreibung || '') : '';
                  
                  if (bildUrl && !bildUrl.startsWith('http') && !bildUrl.startsWith('data:')) {
                    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                    bildUrl = bildUrl.startsWith('/') ? `${backendUrl}${bildUrl}` : `${backendUrl}/${bildUrl}`;
                  }
                  
                  return (
                    <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                      {bildUrl ? (
                        <img 
                          src={bildUrl}
                          alt={`Bild ${index + 1}`}
                          className="w-full h-52 object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-full h-52 bg-gray-100 flex items-center justify-center">
                          <span className="text-gray-400">Kein Bild</span>
                        </div>
                      )}
                      <div className="p-2 bg-gray-50 flex items-center">
                        <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold mr-2">{index + 1}</span>
                        <p className="text-xs text-gray-600 truncate">{bildName || `Schadensbild ${index + 1}`}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                <p className="text-gray-500">Keine Lichtbilder vorhanden</p>
                <p className="text-sm text-gray-400 mt-2">Fotos werden im Bereich "Lichtbilder" hinzugefügt</p>
              </div>
            )}
          </div>

          <DocumentFooter pageNumber={7} />
        </div>

        {/* Weitere Lichtbilder-Seiten (je 4 Bilder pro Seite) */}
        {formData.lichtbilder && formData.lichtbilder.length > 4 && (
          <>
            {/* Seiten für Bilder 5-8, 9-12, etc. */}
            {Array.from({ length: Math.ceil((formData.lichtbilder.length - 4) / 4) }).map((_, pageIndex) => {
              const startIndex = 4 + (pageIndex * 4);
              const endIndex = Math.min(startIndex + 4, formData.lichtbilder.length);
              const pageImages = formData.lichtbilder.slice(startIndex, endIndex);
              
              return (
                <React.Fragment key={`lichtbilder-page-${pageIndex}`}>
                  <div className="page-break"></div>
                  
                  <div className="bg-white shadow-lg relative mt-8" style={{ width: '210mm', minHeight: '297mm', paddingBottom: '85px' }}>
                    <DocumentHeader />
                    
                    <div className="px-10 py-6">
                      <div className="flex items-center mb-6">
                        <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg mr-4">5</div>
                        <h2 className="text-2xl font-bold text-gray-800">Lichtbilder</h2>
                        <span className="ml-2 text-gray-500 text-sm">(Fortsetzung)</span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        {pageImages.map((bild, idx) => {
                          const absoluteIndex = startIndex + idx;
                          let bildUrl = typeof bild === 'string' ? bild : (bild.url || bild.preview || bild.path || '');
                          const bildName = typeof bild === 'object' ? (bild.original_name || bild.name || bild.beschreibung || '') : '';
                          
                          if (bildUrl && !bildUrl.startsWith('http') && !bildUrl.startsWith('data:')) {
                            const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                            bildUrl = bildUrl.startsWith('/') ? `${backendUrl}${bildUrl}` : `${backendUrl}/${bildUrl}`;
                          }
                          
                          return (
                            <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
                              {bildUrl ? (
                                <img 
                                  src={bildUrl}
                                  alt={`Bild ${absoluteIndex + 1}`}
                                  className="w-full h-52 object-cover"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div className="w-full h-52 bg-gray-100 flex items-center justify-center">
                                  <span className="text-gray-400">Kein Bild</span>
                                </div>
                              )}
                              <div className="p-2 bg-gray-50 flex items-center">
                                <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold mr-2">{absoluteIndex + 1}</span>
                                <p className="text-xs text-gray-600 truncate">{bildName || `Schadensbild ${absoluteIndex + 1}`}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <DocumentFooter pageNumber={8 + pageIndex} />
                  </div>
                </React.Fragment>
              );
            })}
          </>
        )}

        {/* Page Break */}
        <div className="page-break"></div>

        {/* Seite 8+: 6. Kalkulation */}
        <div className="bg-white shadow-lg relative mt-8" style={{ width: '210mm', minHeight: '297mm', paddingBottom: '85px' }}>
          <DocumentHeader />
          
          <div className="px-10 py-6">
            {/* Section Header mit Nummer */}
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg mr-4">6</div>
              <h2 className="text-2xl font-bold text-gray-800">Kalkulation</h2>
            </div>
            
            {/* Wertübersicht */}
            <div className="mb-8">
              <h3 className="text-md font-semibold text-gray-700 border-b border-gray-200 pb-2 mb-4">Wertübersicht</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="space-y-3">
                  <div className="flex justify-between border-b border-gray-200 pb-2">
                    <span className="text-gray-600">Wiederbeschaffungswert (brutto):</span>
                    <span className="font-semibold text-gray-800">
                      {typeof formData.wiederbeschaffungswert === 'object' 
                        ? (formData.wiederbeschaffungswert?.durchschnittspreis || '-')
                        : (formData.wiederbeschaffungswert || '-')} €
                    </span>
                  </div>
                  <div className="flex justify-between border-b border-gray-200 pb-2">
                    <span className="text-gray-600">Restwert (brutto):</span>
                    <span className="font-semibold text-gray-800">
                      {typeof formData.restwert === 'object'
                        ? (formData.restwert?.durchschnittspreis || '-')
                        : (formData.restwert_mit_mwst || formData.restwert || '-')} €
                    </span>
                  </div>
                  <div className="flex justify-between border-b border-gray-200 pb-2">
                    <span className="text-gray-600">Reparaturkosten (netto):</span>
                    <span className="font-semibold text-gray-800">
                      {typeof formData.reparaturkosten_ohne_mwst === 'object'
                        ? (formData.reparaturkosten_ohne_mwst?.durchschnittspreis || '-')
                        : (formData.reparaturkosten_ohne_mwst || '-')} €
                    </span>
                  </div>
                  <div className="flex justify-between border-b border-gray-200 pb-2">
                    <span className="text-gray-600">Reparaturkosten (brutto):</span>
                    <span className="font-semibold text-gray-800">
                      {typeof formData.reparaturkosten_mit_mwst === 'object'
                        ? (formData.reparaturkosten_mit_mwst?.durchschnittspreis || '-')
                        : (formData.reparaturkosten_mit_mwst || '-')} €
                    </span>
                  </div>
                  <div className="flex justify-between border-b border-gray-200 pb-2">
                    <span className="text-gray-600">Merkantiler Minderwert:</span>
                    <span className="font-semibold text-gray-800">
                      {typeof formData.minderwert === 'object'
                        ? (formData.minderwert?.durchschnittspreis || '-')
                        : (formData.minderwert || '-')} €
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Nutzungsausfall */}
            <div className="mb-8">
              <h3 className="text-md font-semibold text-gray-700 border-b border-gray-200 pb-2 mb-4">Nutzungsausfall / Mietwagen</h3>
              <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
                <div className="flex">
                  <span className="text-gray-500 w-48">Nutzungsausfall/Tag:</span>
                  <span className="text-gray-800 font-medium">{formData.nutzungsausfall || '-'} €</span>
                </div>
                <div className="flex">
                  <span className="text-gray-500 w-48">Reparaturdauer:</span>
                  <span className="text-gray-800 font-medium">{formData.reparaturdauer || '-'} Tage</span>
                </div>
                <div className="flex">
                  <span className="text-gray-500 w-48">Wiederbeschaffungsdauer:</span>
                  <span className="text-gray-800 font-medium">{formData.wiederbeschaffungsdauer || '-'} Tage</span>
                </div>
                <div className="flex">
                  <span className="text-gray-500 w-48">Mietwagenklasse:</span>
                  <span className="text-gray-800 font-medium">{formData.mietwagenklasse || '-'}</span>
                </div>
              </div>
            </div>

            {/* Schadensumme */}
            <div className="mb-8">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-md font-semibold text-blue-800 mb-3">Schadensumme</h3>
                <div className="flex justify-between text-lg">
                  <span className="text-blue-700">Gesamtschaden:</span>
                  <span className="font-bold text-blue-900">
                    {typeof formData.gesamtschaden === 'object'
                      ? (formData.gesamtschaden?.durchschnittspreis || '-')
                      : typeof formData.reparaturkosten_mit_mwst === 'object'
                        ? (formData.reparaturkosten_mit_mwst?.durchschnittspreis || '-')
                        : (formData.gesamtschaden || formData.reparaturkosten_mit_mwst || '-')} €
                  </span>
                </div>
              </div>
            </div>
          </div>

          <DocumentFooter pageNumber={8} />
        </div>

        {/* Page Break */}
        <div className="page-break"></div>

        {/* Seite 9: 7. Rechnung */}
        <div className="bg-white shadow-lg relative mt-8" style={{ width: '210mm', minHeight: '297mm', paddingBottom: '85px' }}>
          <DocumentHeader />
          
          <div className="px-10 py-6">
            {/* Section Header mit Nummer */}
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg mr-4">7</div>
              <h2 className="text-2xl font-bold text-gray-800">Rechnung</h2>
            </div>
            
            {/* Rechnungsdaten */}
            <div className="mb-6">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex border-b border-gray-200 pb-2">
                  <span className="text-gray-500 w-44 text-sm">Rechnungsnummer:</span>
                  <span className="text-gray-800 font-semibold text-sm">{formData.rechnungsnummer || aktenzeichen || '-'}</span>
                </div>
                <div className="flex">
                  <span className="text-gray-500 w-44 text-sm">Rechnungsdatum:</span>
                  <span className="text-gray-800 font-medium text-sm">{formData.rechnungsdatum || new Date().toLocaleDateString('de-DE')}</span>
                </div>
              </div>
            </div>

            {/* Rechnungspositionen */}
            <div className="mb-8">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-300">
                    <th className="text-left py-2 text-gray-600">Position</th>
                    <th className="text-left py-2 text-gray-600">Beschreibung</th>
                    <th className="text-right py-2 text-gray-600">Betrag</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-200">
                    <td className="py-3">1</td>
                    <td className="py-3">Gutachterhonorar</td>
                    <td className="py-3 text-right font-medium">{formData.gutachterhonorar || '-'} €</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3">2</td>
                    <td className="py-3">Nebenkosten / Fahrtkosten</td>
                    <td className="py-3 text-right font-medium">{formData.nebenkosten || '-'} €</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3">3</td>
                    <td className="py-3">Fotokosten</td>
                    <td className="py-3 text-right font-medium">{formData.fotokosten || '-'} €</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3">4</td>
                    <td className="py-3">Schreibkosten</td>
                    <td className="py-3 text-right font-medium">{formData.schreibkosten || '-'} €</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Summen */}
            <div className="mb-8">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="space-y-2">
                  <div className="flex justify-between border-b border-gray-200 pb-2">
                    <span className="text-gray-600">Zwischensumme (netto):</span>
                    <span className="font-medium text-gray-800">{formData.rechnung_netto || '-'} €</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-200 pb-2">
                    <span className="text-gray-600">MwSt. (19%):</span>
                    <span className="font-medium text-gray-800">{formData.rechnung_mwst || '-'} €</span>
                  </div>
                  <div className="flex justify-between pt-2">
                    <span className="text-lg font-semibold text-gray-800">Gesamtbetrag:</span>
                    <span className="text-lg font-bold text-blue-600">{formData.rechnung_brutto || '-'} €</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Zahlungshinweis */}
            <div className="mb-8">
              <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-600">
                  Bitte überweisen Sie den Rechnungsbetrag innerhalb von 14 Tagen auf das unten angegebene Konto.
                  Bei Rückfragen stehen wir Ihnen gerne zur Verfügung.
                </p>
              </div>
            </div>
          </div>

          <DocumentFooter pageNumber={9} />
        </div>
      </div>

      {/* Action Buttons (nicht drucken) */}
      <div className="no-print flex justify-center gap-4 mt-8 mb-8">
        <Button
          variant="outline"
          className="flex items-center"
          onClick={() => window.print()}
        >
          <Printer className="w-5 h-5 mr-2" />
          Drucken
        </Button>
        <Button
          className="flex items-center bg-blue-600 hover:bg-blue-700 text-white"
          onClick={async () => {
            try {
              const element = document.querySelector('.printable-area');
              if (!element) {
                console.error('Printable area not found');
                return;
              }
              
              const opt = {
                margin: 0,
                filename: `Gutachten_${aktenzeichen || formData.aktenzeichen || 'Neu'}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
                pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
              };
              
              await html2pdf().set(opt).from(element).save();
            } catch (error) {
              console.error('PDF generation error:', error);
            }
          }}
        >
          <Download className="w-5 h-5 mr-2" />
          Als PDF herunterladen
        </Button>
      </div>
    </div>
  );
};

export default LeerFormContent;
export { DocumentHeader, DocumentFooter };
